'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Image from 'next/image';
import { MessageCircle, Users, BookOpen, LayoutDashboard, ChevronLeft, ChevronRight, LogOut, Heart, ShieldCheck, Headphones, Target, BookHeart, Trophy, DollarSign, AlertTriangle, HandHeart, X, Info, UserCircle, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';



interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
  roles: Array<'recovery' | 'volunteer' | 'admin'>;
  highlight?: boolean;
  isChatBadge?: boolean;
}

const navItems: NavItem[] = [
  // Recovery only
  { label: 'Minha Jornada', href: '/recovery-tracking-dashboard', icon: Heart, roles: ['recovery'] },
  { label: 'Missões Diárias', href: '/daily-missions', icon: Target, roles: ['recovery'] },
  { label: 'Modo Crise', href: '/crisis-mode', icon: AlertTriangle, roles: ['recovery'], highlight: true },
  { label: 'Diário Emocional', href: '/emotional-diary', icon: BookHeart, roles: ['recovery'] },
  { label: 'Conquistas', href: '/badges', icon: Trophy, roles: ['recovery'] },
  { label: 'Dinheiro Poupado', href: '/savings-calculator', icon: DollarSign, roles: ['recovery'] },
  { label: 'Chat de Apoio', href: '/live-chat', icon: MessageCircle, roles: ['recovery'], isChatBadge: true },
  { label: 'Fóruns e Grupos de Apoio', href: '/support-groups-forums', icon: Users, roles: ['recovery'] },
  { label: 'Recursos Educativos', href: '/educational-resources', icon: BookOpen, roles: ['recovery'] },
  { label: 'Bloquear Apostas', href: '/autoexclusao', icon: ShieldCheck, roles: ['recovery'] },
  { label: 'Proteção e Controle', href: '/protecao-controle', icon: ShieldAlert, roles: ['recovery'] },

  // Volunteer only
  { label: 'Painel Voluntário', href: '/volunteer-dashboard', icon: Headphones, roles: ['volunteer'] },
  { label: 'Fóruns', href: '/support-groups-forums', icon: Users, roles: ['volunteer'] },
  { label: 'Recursos Educativos', href: '/educational-resources', icon: BookOpen, roles: ['volunteer'] },

  // Admin only
  { label: 'Painel Admin', href: '/admin-panel', icon: LayoutDashboard, roles: ['admin'] },
  { label: 'Atendimento', href: '/live-chat', icon: MessageCircle, roles: ['admin'], isChatBadge: true },
  { label: 'Fóruns', href: '/support-groups-forums', icon: Users, roles: ['admin'] },
  { label: 'Recursos Educativos', href: '/educational-resources', icon: BookOpen, roles: ['admin'] },
  { label: 'Bloquear Apostas', href: '/autoexclusao', icon: ShieldCheck, roles: ['admin'] },
  { label: 'Proteção e Controle', href: '/protecao-controle', icon: ShieldAlert, roles: ['admin'] },
  // All roles
  { label: 'Seja Voluntário', href: '/seja-voluntario', icon: HandHeart, roles: ['admin'] },
  { label: 'Apoie a Página', href: '/apoie-a-pagina', icon: HandHeart, roles: ['recovery', 'volunteer', 'admin'] },
  { label: 'Quem Somos', href: '/quem-somos', icon: Info, roles: ['recovery', 'volunteer', 'admin'] },
  { label: 'Minha Conta', href: '/minha-conta', icon: UserCircle, roles: ['recovery', 'volunteer', 'admin'] },
];

interface SidebarProps {
  userRole?: 'recovery' | 'volunteer' | 'admin';
  userName?: string;
  onClose?: () => void;
}

export default function Sidebar({ userRole = 'recovery', userName = 'Usuário', onClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const pathname = usePathname();
  const router = useRouter();
  const { signOut, user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const supabase = createClient();

    const fetchUnread = async () => {
      // Count conversations where the latest message was NOT sent by the current user
      // and the conversation was updated after the user last visited the chat
      // Since there's no has_unread column, we check for conversations with messages
      // from other senders (volunteer_id is set and != user.id) that are in 'attending' status
      const { data } = await supabase
        .from('chat_conversations')
        .select('id, volunteer_id, updated_at')
        .eq('user_id', user.id)
        .eq('status', 'attending')
        .not('volunteer_id', 'is', null);

      if (!data || data.length === 0) {
        setUnreadChatCount(0);
        return;
      }

      // For each attending conversation, check if there are messages from the volunteer
      // sent after the user's last message
      let unreadCount = 0;
      for (const conv of data) {
        // Get the last message in this conversation
        const { data: messages } = await supabase
          .from('chat_messages')
          .select('sender_id, created_at')
          .eq('conversation_id', conv.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (messages && messages.length > 0) {
          const lastMsg = messages[0];
          // If the last message was sent by someone other than the current user, it's unread
          if (lastMsg.sender_id !== user.id) {
            unreadCount++;
          }
        }
      }
      setUnreadChatCount(unreadCount);
    };

    fetchUnread();

    // Subscribe to realtime changes on chat_messages
    const channel = supabase
      .channel('sidebar-unread')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'chat_messages',
      }, () => {
        fetchUnread();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const visibleItems = navItems.filter(item => item.roles.includes(userRole));
  const initials = userName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const roleLabel = userRole === 'recovery' ? 'Em Recuperação' : userRole === 'volunteer' ? 'Voluntário' : 'Administrador';

  return (
    <aside
      className={`
        relative flex flex-col border-r transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-60'}
        shrink-0 h-screen
      `}
      style={{ backgroundColor: '#1A1A2E', borderColor: 'rgba(124,158,135,0.2)' }}
    >
      {/* Logo */}
      <div
        className={`flex items-center gap-3 px-4 py-5 border-b ${collapsed ? 'justify-center' : 'justify-between'}`}
        style={{ borderColor: 'rgba(124,158,135,0.2)' }}
      >
        {collapsed ? (
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <Image
              src="/assets/images/1dia_transparente-1777268210111.png"
              alt="1"
              width={32}
              height={32}
              className="object-contain"
            />
          </div>
        ) : (
          <Image
            src="/assets/images/logo_1dia_transparente-1777268210110.png"
            alt="1 Dia de Cada Vez — Plataforma de Recuperação"
            width={140}
            height={48}
            className="object-contain"
            style={{ maxHeight: '48px' }}
          />
        )}
        {/* Close button — mobile only */}
        {!collapsed && onClose && (
          <button
            onClick={onClose}
            className="md:hidden p-1.5 rounded-lg hover:opacity-80 transition-colors"
            style={{ color: 'rgba(248,247,244,0.55)' }}
            aria-label="Fechar menu"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Role Badge */}
      {!collapsed && (
        <div
          className="mx-3 mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold text-center"
          style={{
            backgroundColor: userRole === 'recovery' ?'rgba(124,158,135,0.2)'
              : userRole === 'volunteer' ?'rgba(196,168,130,0.2)' :'rgba(248,247,244,0.1)',
            color: userRole === 'recovery' ?'#7C9E87'
              : userRole === 'volunteer' ?'#C4A882' :'#F8F7F4',
          }}
        >
          {roleLabel}
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5 scrollbar-thin">
        {visibleItems.map((item) => {
          const ItemIcon = item.icon;
          const isActive = pathname === item.href;
          const badgeCount = item.isChatBadge ? unreadChatCount : (item.badge ?? 0);
          return (
            <Link
              key={`nav-${item.href}`}
              href={item.href}
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150
                ${collapsed ? 'justify-center' : ''}
              `}
              style={{
                backgroundColor: item.highlight
                  ? isActive ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.12)'
                  : isActive
                  ? 'rgba(124,158,135,0.25)'
                  : 'transparent',
                color: item.highlight
                  ? '#fca5a5'
                  : isActive
                  ? '#7C9E87' :'rgba(248,247,244,0.65)',
              }}
              title={collapsed ? item.label : undefined}
            >
              <ItemIcon size={18} className="shrink-0" />
              {!collapsed && (
                <span className="flex-1 truncate">{item.label}</span>
              )}
              {!collapsed && badgeCount > 0 && (
                <span
                  className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: '#C4A882', color: '#1A1A2E' }}
                >
                  {badgeCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User Footer */}
      <div
        className={`border-t p-3 ${collapsed ? 'flex justify-center' : 'flex items-center gap-3'}`}
        style={{ borderColor: 'rgba(124,158,135,0.2)' }}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
          style={{ backgroundColor: 'rgba(124,158,135,0.3)', color: '#7C9E87' }}
        >
          {initials}
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: '#F8F7F4' }}>{userName}</p>
            <p className="text-xs truncate" style={{ color: 'rgba(248,247,244,0.45)' }}>{roleLabel}</p>
          </div>
        )}
        {!collapsed && (
          <button
            onClick={async () => {
              try { sessionStorage.removeItem('recomeço_user'); } catch {}
              try { await signOut(); } catch {}
              router.push('/');
            }}
            className="p-1.5 rounded-lg transition-colors hover:opacity-80"
            style={{ color: 'rgba(248,247,244,0.45)' }}
            title="Sair"
          >
            <LogOut size={15} />
          </button>
        )}
      </div>

      {/* Collapse Toggle — desktop only */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden md:flex absolute -right-3 top-20 w-6 h-6 rounded-full border items-center justify-center transition-all duration-150 hover:opacity-80 z-10"
        style={{ backgroundColor: '#1A1A2E', borderColor: 'rgba(124,158,135,0.4)', color: '#7C9E87' }}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}